import { useParams, Link } from 'react-router-dom';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import PageMeta from '@/components/base/PageMeta';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { siteInfo } from '@/mocks/siteInfo';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <div className="text-foreground-400 text-lg mb-4">文章未找到</div>
          <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article);

  return (
    <>
      <PageMeta
        title={`${article.title} - 爱情岛`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`${siteInfo.url}/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        articlePublishedTime={article.date}
        articleAuthor={article.author}
        articleSection={article.categoryName}
      />

      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
          { '@type': 'ListItem', position: 2, name: article.categoryName, item: `${siteInfo.url}/category/${article.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      }} />

      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.excerpt,
        image: article.image,
        datePublished: article.date,
        dateModified: article.date,
        author: { '@type': 'Person', name: article.author },
        publisher: { '@type': 'Organization', name: siteInfo.name, url: siteInfo.url },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `${siteInfo.url}/article/${article.id}` },
        articleSection: article.categoryName,
        keywords: article.tags.join(','),
      }} />

      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <article>
            <div className="pt-20 md:pt-24">
              <div className="max-w-4xl mx-auto px-4 md:px-6">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: article.categoryName, href: `/category/${article.categorySlug}` },
                    { label: article.title },
                  ]}
                />
              </div>
            </div>

            <div className="relative w-full h-64 md:h-96 overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
            </div>

            <div className="max-w-3xl mx-auto px-4 md:px-6 -mt-16 relative z-10">
              <div className="bg-background-50 rounded-lg p-6 md:p-10">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full hover:bg-secondary-200 transition-colors whitespace-nowrap"
                  >
                    {article.categoryName}
                  </Link>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                  <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
                </div>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>

                <div className="flex items-center gap-3 pb-6 mb-6 border-b border-background-200/70">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <div className="text-sm font-medium text-foreground-800">{article.author}</div>
                    <div className="text-xs text-foreground-400">资深旅行达人</div>
                  </div>
                </div>

                <div className="prose-custom">
                  {article.content.map((block, index) => {
                    switch (block.type) {
                      case 'paragraph':
                        return (
                          <p key={index} className="text-foreground-700 leading-relaxed mb-5 text-sm md:text-base">
                            {block.value}
                          </p>
                        );
                      case 'heading':
                        return (
                          <h3 key={index} className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mt-8 mb-3">
                            {block.value}
                          </h3>
                        );
                      case 'image':
                        return (
                          <figure key={index} className="my-6 md:my-8">
                            <img
                              src={block.value}
                              alt={block.caption || ''}
                              className="w-full rounded-lg"
                            />
                            {block.caption && (
                              <figcaption className="text-center text-xs text-foreground-400 mt-2">
                                {block.caption}
                              </figcaption>
                            )}
                          </figure>
                        );
                      case 'quote':
                        return (
                          <blockquote key={index} className="border-l-4 border-primary-400 pl-5 py-3 my-6 bg-primary-50/50 rounded-r-lg">
                            <p className="text-foreground-600 italic text-sm md:text-base leading-relaxed">
                              {block.value}
                            </p>
                          </blockquote>
                        );
                      case 'list':
                        return (
                          <ul key={index} className="my-4 space-y-2">
                            {block.items?.map((item, i) => (
                              <li key={i} className="flex items-start gap-2 text-foreground-700 text-sm md:text-base">
                                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                                {item}
                              </li>
                            ))}
                          </ul>
                        );
                      default:
                        return null;
                    }
                  })}
                </div>

                <div className="mt-8 pt-6 border-t border-background-200/70">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-foreground-400">标签：</span>
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-background-100 text-foreground-500 text-xs rounded-full whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-6 flex items-center gap-4 text-xs text-foreground-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line text-sm"></i>
                    {article.viewCount.toLocaleString()} 次阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-heart-line text-sm"></i>
                    {article.likeCount.toLocaleString()} 人喜欢
                  </span>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="py-14 md:py-20 bg-background-100">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-8">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                  {relatedArticles.map((ra) => (
                    <article key={ra.id} className="bg-background-50 rounded-lg overflow-hidden group cursor-pointer">
                      <Link to={`/article/${ra.id}`}>
                        <div className="h-44 overflow-hidden">
                          <img
                            src={ra.image}
                            alt={ra.title}
                            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                      </Link>
                      <div className="p-5">
                        <Link to={`/article/${ra.id}`}>
                          <h3 className="text-base font-heading font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2">
                            {ra.title}
                          </h3>
                        </Link>
                        <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2">{ra.excerpt}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}